import { useMilestone } from "@faire/deprecated-retailer-visitor-shared/stores/domain/useMilestone";
import { useUserStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/useUserStore";
import { Core } from "@faire/design-tokens";
import { IEmailPreferenceGroup } from "@faire/web-api--source/com/faire/email/preferences/IEmailPreferenceGroup";
import { IEmailPreferenceSubgroup } from "@faire/web-api--source/com/faire/email/preferences/IEmailPreferenceSubgroup";
import { IUpdateEmailPreferencesRequest } from "@faire/web-api--source/com/faire/email/preferences/IUpdateEmailPreferencesRequest";
import getEmailPreferences, {
  QueryParameters as EmailPreferencesParams,
} from "@faire/web-api--source/endpoints/www/api/email/preferences/get";
import postEmailPreferences from "@faire/web-api--source/endpoints/www/api/email/preferences/post";
import { useMutation as useMutationPatchUser } from "@faire/web-api--source/endpoints/www/api/user/patch-hooks";
import { trackRetailersOptInNudgeSignUpCtaSuccess } from "@faire/web-api--source/events/retailers/success/optInNudgeSignUpCta";
import { trackRetailersOptInNudgesViewView } from "@faire/web-api--source/events/retailers/view/optInNudgesView";
import { MARKETING_EMAIL_OPT_IN_SUBMITTED } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/MARKETING_EMAIL_OPT_IN_SUBMITTED";
import { MARKETING_SMS_OPT_IN_SUBMITTED } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/MARKETING_SMS_OPT_IN_SUBMITTED";
import { WebApiError } from "@faire/web-api--source/WebApiError";
import { useStrictLocalization } from "@faire/web/common/localization";
import { logError } from "@faire/web/common/logging";
import { Button } from "@faire/web/slate-deprecated/Button";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useState } from "@faire/web/ui/hooks/useState";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { PhoneInput } from "@faire/web/ui/PhoneInput";
import React from "react";
import styled, { css } from "styled-components";

export enum optInOrigin {
  POPUP_PROMPT = "POPUP_PROMPT",
  POST_ORDER_PRIMARY = "POST_ORDER_PRIMARY",
  POST_ORDER_SECONDARY = "POST_ORDER_SECONDARY",
  FOOTER_PAGE = "FOOTER_PAGE",
}

enum PopupType {
  EMAIL = "EMAIL",
  SMS = "SMS",
}

interface IInputProps {
  origin?: keyof typeof optInOrigin;
  onSuccess?: () => void;
  type?: PopupType;
  savedPhoneNumber?: string;
  newFooterXP?: boolean;
  onClick?: () => void;
}

const SMSPostOptedIn: React.FC = () => {
  return (
    <>
      <Typography variant="paragraphSansMedium">
        <StrictLocalMsg
          id="kLb3w5"
          defaultMessage="You’re in!"
          description={{
            text: "Success state shown after user opts into marketing messages",
            img: "https://cdn.faire.com/fastly/d47f8cd0f43f3d81d0358a51e5a43c84257ec7a3a9bba70203801f73990e49c7.png",
          }}
        />
        <StrictLocalMsg
          id="GnrEne"
          defaultMessage="Look for your first SMS from us soon."
          description={{
            text: "Success state shown after user opts into marketing messages",
            img: "https://cdn.faire.com/fastly/d47f8cd0f43f3d81d0358a51e5a43c84257ec7a3a9bba70203801f73990e49c7.png",
          }}
        />
      </Typography>
      <Spacer height="1x" />
    </>
  );
};

const EmailPostOptedIn: React.FC = () => {
  return (
    <>
      <Typography variant="paragraphSansMedium">
        <StrictLocalMsg
          id="noNqXt"
          defaultMessage="You’re in! Look for your first email soon."
          description={{
            text: "Opt-in nudge banner: ",
            img: "https://cdn.faire.com/fastly/f4abe86964205cc4d577fbd05bb20d1513e52c4d25b85a6bface16f6d4d05fce.png",
          }}
        />
      </Typography>
      <Spacer height="1x" />
    </>
  );
};

export const OptInNudgeInput: React.FC<IInputProps> = ({
  origin,
  onSuccess,
  type = PopupType.EMAIL,
  savedPhoneNumber,
  newFooterXP,
  onClick,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const { emailAddress, userToken } = useUserStore();
  const { setHasOccurred: setOptInSubmitted } = useMilestone(
    type === PopupType.EMAIL
      ? MARKETING_EMAIL_OPT_IN_SUBMITTED
      : MARKETING_SMS_OPT_IN_SUBMITTED
  );

  const [error, setError] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasSuccessfullyOptedIn, setHasSuccessfullyOptedIn] =
    useState<boolean>(false);
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>(
    savedPhoneNumber
  );

  const ref = useDoActionWhenVisible(() => {
    if (origin) {
      trackRetailersOptInNudgesViewView(origin);
    }
  });

  const unsubscribeToAllEmailPreferenceGroups = (
    emailPreferenceGroups: IEmailPreferenceGroup[]
  ): IEmailPreferenceGroup[] => {
    const emailPreferenceGroupsClone = emailPreferenceGroups;

    emailPreferenceGroupsClone.forEach(
      (emailPreferenceGroup: IEmailPreferenceGroup) => {
        emailPreferenceGroup.subgroups.forEach(
          (subgroup: IEmailPreferenceSubgroup) => {
            subgroup.is_unsubscribed = false;
          }
        );
      }
    );

    return emailPreferenceGroupsClone;
  };

  const invalidPhoneNumberError = strictLocalize({
    id: "FPcbu0",
    defaultMessage: "Please enter a valid phone number",
  });

  const otherError = strictLocalize({
    id: "0Abtfk",
    defaultMessage: "Something went wrong—please try again.",
    description: {
      text: "Fallback error message given after submitting an email address to opt into marketing emails",
      img: "https://cdn.faire.com/fastly/d903afa775cffef408d6aef49c31436919b9fce1c06bda893913d873dd40ba8a.png",
    },
  });

  const { mutate: updateUser, isPending: isUpdatingUser } =
    useMutationPatchUser({
      onSuccess: () => {
        if (origin) {
          trackRetailersOptInNudgeSignUpCtaSuccess(origin);
        }

        setOptInSubmitted();
        setHasSuccessfullyOptedIn(true);

        if (onSuccess) {
          onSuccess();
        }

        setIsLoading(false);
      },
      onError: (error) => {
        logError(error);
        if (
          (error as WebApiError).responseData?.service_error_code ===
          "UserValidationException.INVALID_PHONE_NUMBER"
        ) {
          setError(invalidPhoneNumberError);
        } else {
          setError(otherError);
        }
        setIsLoading(false);
      },
    });

  const handleSMSOptInSubmission = async () => {
    setError(undefined);
    setIsLoading(true);

    if (origin === optInOrigin.POST_ORDER_PRIMARY) {
      onClick?.();
    }

    updateUser([
      {
        token: userToken,
        phone_number: phoneNumber,
        marketing_sms_preference_change:
          "MARKETING_SMS_PREFERENCE_CHANGE_OPT_IN",
      },
    ]);
  };

  const handleEmailSubmission = async () => {
    setError(undefined);
    setIsLoading(true);

    if (origin === optInOrigin.POST_ORDER_PRIMARY) {
      onClick?.();
    }

    try {
      const { email_preference_groups } = await getEmailPreferences(
        EmailPreferencesParams.build({
          outgoing_email: emailAddress,
        })
      );

      if (!email_preference_groups) {
        setError(otherError);
        return;
      }

      await postEmailPreferences(
        IUpdateEmailPreferencesRequest.build({
          email_preference_groups: unsubscribeToAllEmailPreferenceGroups(
            email_preference_groups
          ),
          unsubscribe_from_all: false,
        })
      );

      if (origin) {
        trackRetailersOptInNudgeSignUpCtaSuccess(origin);
      }

      setOptInSubmitted();
      setHasSuccessfullyOptedIn(true);

      if (onSuccess) {
        onSuccess();
      }
    } catch (e) {
      logError(e);
      setError(otherError);
    } finally {
      setIsLoading(false);
    }
  };

  if (hasSuccessfullyOptedIn) {
    return type === PopupType.SMS ? <SMSPostOptedIn /> : <EmailPostOptedIn />;
  }

  if (type === PopupType.SMS) {
    return (
      <div ref={ref}>
        <PhoneInput
          label={strictLocalize({
            id: "WJpIG3",
            defaultMessage: "Mobile phone number",
            description: {
              text: "Heading",
              img: "https://cdn.faire.com/fastly/97ba0cfebdc07f950435267082d9513b79800dea130f8ea09ae3161b269471e4.png",
            },
          })}
          helper={strictLocalize({
            id: "72Jtlj",
            defaultMessage: "Standard messaging rates apply. Opt out anytime.",
            description: {
              text: "Warning when sending sms to an user",
              img: "https://cdn.faire.com/fastly/b923bb104c4adca8a9d0f29149510ced0944e5ee8203c414f0b0041abf1cd103.png",
            },
          })}
          error={error}
          onChangePhoneNumber={setPhoneNumber}
          defaultPhoneNumber={phoneNumber}
        />
        <Spacer height="2x" />
        <StyledButton
          newFooterXP={newFooterXP}
          variant={newFooterXP ? "secondary" : "primary"}
          loading={isLoading}
          onClick={handleSMSOptInSubmission}
        >
          <StrictLocalMsg
            id="1RUD5s"
            defaultMessage="Opt into SMS"
            description={{
              text: "Opt-in nudge banner: marketing text messages sign up CTA",
              img: "https://cdn.faire.com/fastly/b923bb104c4adca8a9d0f29149510ced0944e5ee8203c414f0b0041abf1cd103.png",
            }}
          />
        </StyledButton>
      </div>
    );
  }

  return (
    <div ref={ref}>
      <Typography variant="paragraphSansRegular">
        <StrictLocalMsg
          id="0wcaRo"
          defaultMessage="Email address"
          description={{
            text: "Opt-in nudge banner: email input label",
            img: "https://cdn.faire.com/fastly/d903afa775cffef408d6aef49c31436919b9fce1c06bda893913d873dd40ba8a.png",
          }}
        />
      </Typography>
      <Typography variant="paragraphSansMedium">{emailAddress}</Typography>
      <Spacer height="2x" />
      {error ? (
        <>
          <Typography variant="labelSansRegular" color={Core.text.critical}>
            {error}
          </Typography>
          <Spacer height="1x" />
        </>
      ) : null}
      <StyledButton
        variant={newFooterXP ? "secondary" : "primary"}
        loading={isLoading || isUpdatingUser}
        newFooterXP={newFooterXP}
        onClick={handleEmailSubmission}
      >
        <StrictLocalMsg
          id="PnMTXY"
          defaultMessage="Opt in to email"
          description={{
            text: "Opt-in nudge banner: marketing emails sign up CTA",
            img: "https://cdn.faire.com/fastly/d903afa775cffef408d6aef49c31436919b9fce1c06bda893913d873dd40ba8a.png",
          }}
        />
      </StyledButton>
    </div>
  );
};

const StyledButton = styled(Button)<{ newFooterXP?: boolean }>`
  width: 100%;
  ${({ newFooterXP }) =>
    newFooterXP
      ? css`
          width: fit-content;
        `
      : ""}
`;
